import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectEditorMoveNode,
  selectFilteredEdges,
  selectFilteredNodes,
  selectFilteredPoints,
} from '../map/slice/mapSelectors';
import {
  edgeToGeoJson,
  FeatureLike,
  PropertyExtractor,
} from '../utils/geoJsonUtils';
import { useTheme } from '../helpers/hooks';
import { Edge } from '../types';
import { fromLonLat } from 'ol/proj';
import {
  selectNodeSize,
  selectShowPointCapacityComponents,
  selectShowSegmentCapacityComponents,
} from '../plotter/plotterSelectors';
import { useMapLayer } from '../hooks/useMapLayer';

const SEGMENT_CAPACITY_STROKE_COLOR = '#FF000080';
const POINT_CAPACITY_STROKE_COLOR = '#FF000080';
const POINT_CAPACITY_FILL_COLOR = '#FF000040';

const getEdgeStyle = (nodeSize: number) => [
  {
    filter: [
      'all',
      ['==', ['get', 'type'], 'edge'],
      ['<=', ['get', 'head'], 0],
    ],
    style: {
      'stroke-width': 10,
      'stroke-color': SEGMENT_CAPACITY_STROKE_COLOR,
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
      'stroke-line-dash': [15, 10, 15, 10],
      'stroke-line-dash-offset': 0,
    },
  },
  {
    filter: ['all', ['==', ['get', 'type'], 'edge'], ['>', ['get', 'head'], 0]],
    style: {
      'shape-points': 3,
      'shape-radius': 12,
      'shape-fill-color': SEGMENT_CAPACITY_STROKE_COLOR,
      'shape-stroke-color': SEGMENT_CAPACITY_STROKE_COLOR,
      'shape-stroke-width': 4,
      'shape-rotate-with-view': false,
      'shape-displacement': [0, 0],
      'shape-opacity': 1,
      'shape-angle': ['get', 'angle'],
    },
  },
  {
    filter: ['==', ['get', 'type'], 'node'],
    style: {
      'circle-radius': nodeSize * 2.2,
      'circle-fill-color': POINT_CAPACITY_FILL_COLOR,
      'circle-stroke-color': POINT_CAPACITY_STROKE_COLOR,
      'circle-stroke-width': 2,
      'circle-rotate-with-view': false,
      'circle-displacement': [0, 0],
    },
  },
];

const extractEdgeProps: PropertyExtractor<Edge> = (edge) => {
  return {
    key: `edge-${edge.id}`,
    id: edge.id,
    type: 'edge',
  };
};

export const MapCapacityComponents = ({ zIndex = 1 }) => {
  const edges = useSelector(selectFilteredEdges);
  const points = useSelector(selectFilteredPoints);
  const nodes = useSelector(selectFilteredNodes);
  const editorMoveNode = useSelector(selectEditorMoveNode);
  const nodeSize = useSelector(selectNodeSize);
  const showSegmentCapacityComponents = useSelector(
    selectShowSegmentCapacityComponents,
  );
  const showPointCapacityComponents = useSelector(
    selectShowPointCapacityComponents,
  );

  const [theme] = useTheme();

  const edgeFeatures = useMemo(
    () =>
      showSegmentCapacityComponents
        ? Object.values(edges)
            .filter((edge) => {
              if (edge?.positions?.length < 2) {
                return false;
              }
              if (
                edge.start_node.toString() === editorMoveNode?.toString() ||
                edge.end_node.toString() === editorMoveNode?.toString()
              ) {
                return false;
              }
              return true;
            })
            .flatMap((edge) => {
              const features = [];
              if (edge.backhaul_capacity_components?.length > 0) {
                features.push(
                  ...edgeToGeoJson(
                    {
                      ...edge,
                      positions: [edge.positions[1], edge.positions[0]],
                    },
                    extractEdgeProps,
                    true,
                    {},
                    theme,
                  ),
                );
              }
              if (edge.forwardhaul_capacity_components?.length > 0) {
                features.push(
                  ...edgeToGeoJson(edge, extractEdgeProps, true, {}, theme),
                );
              }
              return features;
            })
        : [],
    [edges, theme, editorMoveNode, showSegmentCapacityComponents],
  );

  const pointFeatures: FeatureLike[] = useMemo(
    () =>
      showPointCapacityComponents
        ? Object.values(nodes)
            .filter((node) =>
              node?.points
                .map((pointId) => points[pointId])
                .filter(Boolean)
                .some((point) => point.capacity_components?.length > 0),
            )
            .map(
              (node): FeatureLike => ({
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: fromLonLat([
                    Number(node.lng ?? 0),
                    Number(node.lat ?? 0),
                  ]),
                },
                properties: {
                  id: node.id,
                  key: `node-${node.id}`,
                  type: 'node',
                },
              }),
            )
        : [],
    [nodes, points, showPointCapacityComponents],
  );

  const features = useMemo(
    () => edgeFeatures.concat(pointFeatures),
    [edgeFeatures, pointFeatures],
  );

  const style = useMemo(() => getEdgeStyle(Number(nodeSize) ?? 0), [nodeSize]);

  useMapLayer(features, { zIndex, style });

  return <></>;
};
